// iot-admin/client/src/pages/MomentsKeyDetail.js
// Moments API Key detail page — single key view with overview, usage, billing.
// Mirrors GGOSDeviceDetail.js quality bar.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { momentsAdminService } from '../services/api';
import {
  ArrowLeft, Key, Shield, XCircle, AlertTriangle, Clock,
  RotateCw, Copy, RefreshCw, BarChart3, IndianRupee, Zap,
  Activity, Globe, Link2, Lock, Eye, Building2, Trash2,
} from 'lucide-react';
import './MomentsKeyList.css';

/* ── helpers ── */
const fmt = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fullDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const relDate = (d) => {
  if (!d) return 'Never';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
const copyText = (text) => { navigator.clipboard.writeText(text).catch(() => {}); };

const Badge = ({ color, children }) => (
  <span className={`mk-badge mk-badge--${color}`}>{children}</span>
);

const PLAN_META = {
  explorer: { color: 'blue', label: 'Explorer', limit: '1,000/day', price: 'Free' },
  growth: { color: 'green', label: 'Growth', limit: '10,000/day', price: '₹0.50/1K' },
  enterprise: { color: 'purple', label: 'Enterprise', limit: 'Unlimited', price: 'Custom' },
};

const STATUS_META = {
  active: { color: 'green', icon: Shield, label: 'Active' },
  revoked: { color: 'red', icon: XCircle, label: 'Revoked' },
  expired: { color: 'yellow', icon: AlertTriangle, label: 'Expired' },
};

/* ── Detail Field ── */
const Field = ({ label, children, mono }) => (
  <div className="mkd-field">
    <span className="mkd-field-label">{label}</span>
    <span className={`mkd-field-value ${mono ? 'mkd-mono' : ''}`}>{children || '—'}</span>
  </div>
);

/* ════════════════════════════════════════════════════════
   Main component
   ════════════════════════════════════════════════════════ */
const MomentsKeyDetail = () => {
  const { keyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tenantId = searchParams.get('tenant') || '';

  const [keyData, setKeyData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchKey = useCallback(async () => {
    if (!tenantId || !keyId) return;
    try {
      setLoading(true);
      const res = await momentsAdminService.getKey(tenantId, keyId);
      setKeyData(res.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load key');
    } finally {
      setLoading(false);
    }
  }, [keyId, tenantId]);

  const fetchUsage = useCallback(async () => {
    if (!tenantId || !keyId) return;
    try {
      const res = await momentsAdminService.getKeyUsage(tenantId, keyId);
      setUsage(res.data);
    } catch { /* non-fatal */ }
  }, [keyId, tenantId]);

  const fetchBilling = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await momentsAdminService.getBillingSummary(tenantId);
      // Find this key's billing from the summary
      const keyBilling = (res.data?.keys || []).find(k => k.api_key_id === keyId);
      setBilling(keyBilling || null);
    } catch { /* non-fatal */ }
  }, [keyId, tenantId]);

  useEffect(() => { fetchKey(); }, [fetchKey]);
  useEffect(() => {
    if (activeTab === 'usage') fetchUsage();
    if (activeTab === 'billing') fetchBilling();
  }, [activeTab, fetchUsage, fetchBilling]);

  useEffect(() => {
    if (message) { const t = setTimeout(() => setMessage(null), 5000); return () => clearTimeout(t); }
  }, [message]);

  const handleRevoke = async () => {
    if (!window.confirm(`Revoke key "${keyData?.name}" (${keyData?.key_prefix}…)? This cannot be undone.`)) return;
    try {
      await momentsAdminService.revokeKey(tenantId, keyId, 'Super admin revocation');
      setMessage('Key revoked');
      fetchKey();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Revoke failed');
    }
  };

  const handleRotate = async () => {
    if (!window.confirm(`Rotate key "${keyData?.name}"? The old key will stop working immediately.`)) return;
    try {
      const data = await momentsAdminService.rotateKey(tenantId, keyId);
      const newKey = data.data?.api_key;
      if (newKey) {
        await navigator.clipboard.writeText(newKey).catch(() => {});
        setMessage(`Key rotated! New key: ${newKey.substring(0, 25)}… (copied to clipboard)`);
      }
      fetchKey();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Rotate failed');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="mk-loading" style={{ marginTop: '4rem' }}>
        <div className="mk-spinner" />
        <span>Loading key details…</span>
      </div>
    );
  }

  if (error && !keyData) {
    return (
      <div className="mk-page">
        <div className="mk-alert mk-alert--error">{error}</div>
        <button onClick={() => navigate('/moments')} className="mk-btn mk-btn--secondary">
          <ArrowLeft size={16} /> Back to Keys
        </button>
      </div>
    );
  }

  const sm = STATUS_META[keyData?.status] || STATUS_META.active;
  const pm = PLAN_META[keyData?.plan] || PLAN_META.explorer;
  const StatusIcon = sm.icon;

  return (
    <div className="mk-page mkd-page">
      {/* ── Header ── */}
      <header className="mkd-header">
        <div className="mkd-header-left">
          <button onClick={() => navigate('/moments')} className="mkd-back-btn" title="Back to Keys">
            <ArrowLeft size={18} />
          </button>
          <Key size={20} className="mkd-icon" />
          <div>
            <h1 className="mkd-name">{keyData?.name || 'API Key'}</h1>
            <span className="mkd-prefix">{keyData?.key_prefix}…</span>
          </div>
          <Badge color={sm.color}><StatusIcon size={11} /> {sm.label}</Badge>
          <Badge color={pm.color}>{pm.label}</Badge>
        </div>
        <div className="mkd-header-right">
          {keyData?.status === 'active' && (
            <>
              <button className="mk-btn mk-btn--secondary" onClick={handleRotate}><RotateCw size={14} /> Rotate</button>
              <button className="mk-btn mk-btn--danger" onClick={handleRevoke}><XCircle size={14} /> Revoke</button>
            </>
          )}
          <button className="mk-btn mk-btn--secondary" onClick={fetchKey}><RefreshCw size={14} /></button>
        </div>
      </header>

      {message && <div className="mk-alert mk-alert--success mk-page-alert">{message}</div>}
      {error && <div className="mk-alert mk-alert--error mk-page-alert">{error}</div>}

      {/* ── Tabs ── */}
      <div className="mkd-tabs">
        <button className={`mkd-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <Eye size={14} /> Overview
        </button>
        <button className={`mkd-tab ${activeTab === 'usage' ? 'active' : ''}`} onClick={() => setActiveTab('usage')}>
          <BarChart3 size={14} /> Usage
        </button>
        <button className={`mkd-tab ${activeTab === 'billing' ? 'active' : ''}`} onClick={() => setActiveTab('billing')}>
          <IndianRupee size={14} /> Billing
        </button>
      </div>

      {/* ── Overview ── */}
      {activeTab === 'overview' && (
        <div className="mkd-section">
          <div className="mkd-card">
            <h3><Building2 size={16} /> Tenant & Identity</h3>
            <div className="mkd-fields">
              <Field label="Tenant">{keyData?.tenant_name || keyData?.tenant_id}</Field>
              <Field label="Tenant ID" mono>{keyData?.tenant_id}</Field>
              <Field label="Key ID" mono>{keyData?.id}</Field>
              <Field label="Key Prefix" mono>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  {keyData?.key_prefix}…
                  <button className="mk-btn-icon" onClick={() => copyText(keyData?.key_prefix)} title="Copy prefix"><Copy size={12} /></button>
                </span>
              </Field>
              <Field label="Created By">{keyData?.created_by_email || '—'}</Field>
            </div>
          </div>

          <div className="mkd-card">
            <h3><Key size={16} /> Key Configuration</h3>
            <div className="mkd-fields">
              <Field label="Status"><Badge color={sm.color}><StatusIcon size={11} /> {sm.label}</Badge></Field>
              <Field label="Plan"><Badge color={pm.color}>{pm.label}</Badge></Field>
              <Field label="Billable">{keyData?.billable ? 'Yes' : 'No'}</Field>
              <Field label="Price Per 1K">{fmtINR(keyData?.price_per_1k_requests)}</Field>
            </div>
          </div>

          <div className="mkd-card">
            <h3><Zap size={16} /> Rate Limits & Quotas</h3>
            <div className="mkd-fields">
              <Field label="Rate Limit">{keyData?.rate_limit_per_second ? `${fmt(keyData.rate_limit_per_second)}/sec` : 'Default'}</Field>
              <Field label="Burst Limit">{keyData?.burst_limit ? fmt(keyData.burst_limit) : 'Same as rate limit'}</Field>
              <Field label="Daily Quota">{keyData?.daily_quota ? `${fmt(keyData.daily_quota)} requests` : 'Unlimited'}</Field>
              <Field label="Monthly Quota">{keyData?.monthly_quota ? `${fmt(keyData.monthly_quota)} requests` : 'Unlimited'}</Field>
            </div>
          </div>

          <div className="mkd-card">
            <h3>Access & Security</h3>
            <div className="mkd-fields">
              <Field label="Allowed Origins">
                {keyData?.allowed_origins?.length > 0
                  ? keyData.allowed_origins.map((o, i) => <code key={i} className="mkd-origin">{o}</code>)
                  : <span className="mk-muted">All origins (unrestricted)</span>}
              </Field>
              <Field label="Allowed Endpoints">
                {keyData?.allowed_endpoints?.length > 0
                  ? keyData.allowed_endpoints.map((e, i) => <code key={i} className="mkd-origin">{e}</code>)
                  : <span className="mk-muted">All endpoints</span>}
              </Field>
              <Field label="Scopes">
                {keyData?.scopes?.length > 0
                  ? keyData.scopes.join(', ')
                  : <span className="mk-muted">Default scopes</span>}
              </Field>
            </div>
          </div>

          <div className="mkd-card">
            <h3>Timestamps</h3>
            <div className="mkd-fields">
              <Field label="Created">{fullDate(keyData?.created_at)}</Field>
              <Field label="Last Used">{keyData?.last_used_at ? `${fullDate(keyData.last_used_at)} (${relDate(keyData.last_used_at)})` : 'Never'}</Field>
              <Field label="Expires">{keyData?.expires_at ? fullDate(keyData.expires_at) : 'Never'}</Field>
              <Field label="Updated">{fullDate(keyData?.updated_at)}</Field>
            </div>
          </div>

          {keyData?.description && (
            <div className="mkd-card">
              <h3>Description</h3>
              <p className="mkd-description">{keyData.description}</p>
            </div>
          )}

          {keyData?.revoked_at && (
            <div className="mkd-card mkd-card--danger">
              <h3><XCircle size={16} /> Revocation Details</h3>
              <div className="mkd-fields">
                <Field label="Revoked At">{fullDate(keyData.revoked_at)}</Field>
                <Field label="Revoked By">{keyData.revoked_by || '—'}</Field>
                <Field label="Reason">{keyData.revoke_reason || 'No reason provided'}</Field>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Usage ── */}
      {activeTab === 'usage' && (
        <div className="mkd-section">
          {!usage ? (
            <div className="mk-loading"><div className="mk-spinner" /> Loading usage…</div>
          ) : (
            <>
              <div className="mkd-stats-row">
                <div className="mkd-stat-card">
                  <Zap size={18} className="mkd-stat-icon mkd-stat-icon--blue" />
                  <div className="mkd-stat-val">{fmt(usage.total_requests)}</div>
                  <div className="mkd-stat-lbl">Total Requests</div>
                </div>
                <div className="mkd-stat-card">
                  <Activity size={18} className="mkd-stat-icon mkd-stat-icon--green" />
                  <div className="mkd-stat-val">{fmt(usage.billable_requests)}</div>
                  <div className="mkd-stat-lbl">Billable</div>
                </div>
                <div className="mkd-stat-card">
                  <IndianRupee size={18} className="mkd-stat-icon mkd-stat-icon--purple" />
                  <div className="mkd-stat-val">{fmtINR(usage.total_billed)}</div>
                  <div className="mkd-stat-lbl">Amount Billed</div>
                </div>
                <div className="mkd-stat-card">
                  <Globe size={18} className="mkd-stat-icon mkd-stat-icon--teal" />
                  <div className="mkd-stat-val">{fmt(usage.total_cells_queried)}</div>
                  <div className="mkd-stat-lbl">Cells Queried</div>
                </div>
              </div>

              {usage.daily?.length > 0 && (
                <div className="mkd-card">
                  <h3><BarChart3 size={16} /> Daily Usage (Last 30 Days)</h3>
                  <div className="mkd-usage-chart">
                    {(() => {
                      const daily = usage.daily || [];
                      const maxReq = Math.max(...daily.map(d => Number(d.total_requests || 0)), 1);
                      return daily.slice(-14).map(d => {
                        const pct = (Number(d.total_requests || 0) / maxReq) * 100;
                        return (
                          <div key={d.usage_date} className="mkd-bar-row">
                            <span className="mkd-bar-date">{new Date(d.usage_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>
                            <div className="mkd-bar-track"><div className="mkd-bar-fill" style={{ width: `${Math.max(pct, 1)}%` }} /></div>
                            <span className="mkd-bar-count">{fmt(d.total_requests)}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {(!usage.daily || usage.daily.length === 0) && (
                <div className="mk-empty" style={{ marginTop: '2rem' }}>
                  <BarChart3 size={40} />
                  <p>No usage data yet</p>
                  <span className="mk-empty-hint">Start making API calls to see usage statistics</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Billing ── */}
      {activeTab === 'billing' && (
        <div className="mkd-section">
          {billing ? (
            <div className="mkd-card">
              <h3><IndianRupee size={16} /> Billing Summary (Last 30 Days)</h3>
              <div className="mkd-fields">
                <Field label="Total Requests">{fmt(billing.total_requests)}</Field>
                <Field label="Billable Requests">{fmt(billing.billable_requests)}</Field>
                <Field label="Cells Queried">{fmt(billing.total_cells)}</Field>
                <Field label="Amount Billed">{fmtINR(billing.total_billed)}</Field>
                <Field label="Price Per 1K">{fmtINR(billing.price_per_1k_requests)}</Field>
                <Field label="Billable">{billing.billable ? 'Yes' : 'No'}</Field>
              </div>
            </div>
          ) : (
            <div className="mk-empty" style={{ marginTop: '2rem' }}>
              <IndianRupee size={40} />
              <p>No billing data available</p>
              <span className="mk-empty-hint">Billing data will appear after billable API usage</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MomentsKeyDetail;
